import React, {useContext, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {CrispContext, type CrispContextValue} from './CrispCtx';
import {useExtensionConnection} from '../hooks/extension/ExtensionProvider';
import {CrispClient} from '../services';

export function useCrisp(): CrispContextValue {
  const context = useContext(CrispContext);
  if (context === undefined) {
    throw new Error('useCrisp must be used within a CrispProvider');
  }
  return context;
}

interface CrispProviderProps {
  children: React.ReactNode;
}

export function CrispProvider({children}: CrispProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [crispService] = useState(() => new CrispClient());
  // Écouter les changements de version de l'extension
  const {version: extensionVersion, isConnected: isExtensionConnected} = useExtensionConnection();

  // Tracker l'extension quand la version change
  useEffect(() => {
    if (extensionVersion && isExtensionConnected) {
      crispService.trackExtension(extensionVersion);
    }
  }, [extensionVersion, isExtensionConnected, crispService]);

  // Gérer l'ouverture automatique du support via l'URL ?open_support_chat=1
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('open_support_chat') === '1') {
      // Supprimer le paramètre de l'URL
      urlParams.delete('open_support_chat');
      const newSearch = urlParams.toString();
      const newHash = location.hash;

      // Remplacer l'URL dans l'historique sans recharger la page
      navigate(
        {
          pathname: location.pathname,
          search: newSearch ? `?${newSearch}` : '',
          hash: newHash,
        },
        {replace: true},
      );

      // Ouvrir le support Crisp
      crispService.openChat();
    }
  }, [location, navigate, crispService]);

  const contextValue: CrispContextValue = {
    crisp: crispService,
  };

  return <CrispContext.Provider value={contextValue}>{children}</CrispContext.Provider>;
}
